import {
  forwardRef,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "@hey404/ui";
import { titleClass } from "./titles";
import {
  btnDanger,
  btnPrimary,
  btnSecondary,
  inputClass,
  kickerClass,
} from "./admin-ui";

/**
 * Platform components for the V30 web design system. They wrap the shared
 * class strings in `admin-ui` so the component and class-string call sites
 * render the same thing.
 */

const buttonTones = {
  primary: btnPrimary,
  secondary: btnSecondary,
  danger: btnDanger,
} as const;

// V30 ships one size for actions (48px tall) and a compact one that still
// keeps the 44px touch target.
const buttonSizes = {
  md: "min-h-12 px-5 py-3 text-base",
  sm: "min-h-11 px-3.5 py-2 text-sm",
} as const;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof buttonTones;
  size?: keyof typeof buttonSizes;
  full?: boolean;
}

export function Button({
  variant = "primary",
  size = "md",
  full,
  type = "button",
  className,
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cn(
        buttonTones[variant],
        buttonSizes[size],
        full && "w-full",
        className,
      )}
      {...props}
    />
  );
}

export type InputProps = InputHTMLAttributes<HTMLInputElement>;

// No top margin: the component is placed by its label, unlike the bare
// `inputClass` which still carries `mt-1` for its older call sites.
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref) => (
    <input ref={ref} className={cn(inputClass, "mt-0", className)} {...props} />
  ),
);
Input.displayName = "Input";

export function Kicker({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn(kickerClass, className)} {...props} />;
}

export { titleClass };

// ── Personality layer ──────────────────────────────────────────────────────
// The landing's playful pieces, under V30's rules: one protagonist doodle per
// scene plus one or two supporting ones, kept on edges and corners, never over
// text or controls, and invisible to assistive tech.

/** The original V30 brand SVGs, served from /public/elementos. */
export type DoodleName =
  | "flower-pink"
  | "flower-yellow"
  | "face-lime"
  | "face-lavender"
  | "cursor-cream"
  | "cursor-lavender"
  | "cursor-pink"
  | "key-cream"
  | "key-lavender"
  | "question-yellow"
  | "exclamation-pink";

export interface DoodleProps {
  name: DoodleName;
  /** Resting tilt in degrees; the float swings to the mirrored angle. */
  tilt?: number;
  float?: boolean;
  /** Seconds of offset so neighbouring doodles don't move in lockstep. */
  delay?: number;
  className?: string;
}

export function Doodle({
  name,
  tilt = -4,
  float = false,
  delay = 0,
  className,
}: DoodleProps) {
  return (
    <img
      src={`/elementos/${name}.svg`}
      alt=""
      aria-hidden="true"
      draggable={false}
      style={
        {
          "--doodle-tilt": `${tilt}deg`,
          animationDelay: `${delay}s`,
        } as CSSProperties
      }
      className={cn(
        "doodle pointer-events-none h-auto select-none",
        float && "doodle-float",
        className,
      )}
    />
  );
}

const stickerTones = {
  lemon: "bg-lemon",
  acid: "bg-acid",
  bubble: "bg-bubble",
  grape: "bg-grape",
  paper: "bg-paper",
} as const;

export interface StickerProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: keyof typeof stickerTones;
  /** Tilt in degrees, the landing's ±2–3° paper rotation. */
  tilt?: number;
}

/** A small tilted paper label, like the landing's kicker stickers. */
export function Sticker({
  tone = "lemon",
  tilt = -2,
  className,
  style,
  ...props
}: StickerProps) {
  return (
    <span
      style={{ rotate: `${tilt}deg`, ...style }}
      className={cn(
        "border-ink border-chip shadow-brutal sticker-wiggle inline-block px-3 py-1 font-mono text-xs font-bold uppercase tracking-[0.08em]",
        stickerTones[tone],
        className,
      )}
      {...props}
    />
  );
}

/** Marker-pen highlight behind a short span of a title (V30 `h4-highlight`). */
export function Marker({ children }: { children: ReactNode }) {
  return <span className="marker">{children}</span>;
}

const windowTones = {
  lemon: "bg-lemon",
  acid: "bg-acid",
  bubble: "bg-bubble",
  grape: "bg-grape",
} as const;

/**
 * The landing's recurring browser-window title bar: coloured strip, three
 * traffic-light dots and an optional mono caption. Sits at the top of a
 * bordered panel.
 */
export function WindowBar({
  tone = "grape",
  title,
  className,
}: {
  tone?: keyof typeof windowTones;
  title?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "border-ink border-b-(length:--border-surface) flex items-center gap-2 px-4 py-3",
        windowTones[tone],
        className,
      )}
    >
      <span
        aria-hidden="true"
        className="border-ink bg-bubble border-chip block size-3.5 rounded-full"
      />
      <span
        aria-hidden="true"
        className="border-ink bg-lemon border-chip block size-3.5 rounded-full"
      />
      <span
        aria-hidden="true"
        className="border-ink bg-acid border-chip block size-3.5 rounded-full"
      />
      {title && (
        <span className="text-ink ml-2 truncate font-mono text-xs font-bold tracking-wider">
          {title}
        </span>
      )}
    </div>
  );
}

export interface PageHeaderProps {
  kicker: string;
  title: ReactNode;
  description?: ReactNode;
  doodle?: DoodleName;
  tone?: StickerProps["tone"];
  className?: string;
}

/**
 * Title block for platform pages: tilted sticker kicker, display title and an
 * optional brand doodle floating beside it (hidden on small screens, where it
 * would crowd the title).
 */
export function PageHeader({
  kicker,
  title,
  description,
  doodle,
  tone = "lemon",
  className,
}: PageHeaderProps) {
  return (
    <header className={cn("relative pr-0 sm:pr-28", className)}>
      <Sticker tone={tone}>{kicker}</Sticker>
      <h1 className={cn(titleClass.page, "mt-4")}>{title}</h1>
      {description && <p className="text-ink-soft mt-3">{description}</p>}
      {doodle && (
        <Doodle
          name={doodle}
          float
          tilt={6}
          className="absolute -top-2 right-0 hidden w-20 sm:block"
        />
      )}
    </header>
  );
}

// ── Collage pieces ─────────────────────────────────────────────────────────
// Scrapbook vocabulary from the brand references: washi tape, torn paper
// notes, hand-drawn strokes and sparkles. Strokes use currentColor so a parent
// `text-*` token colours them.

const washiTones = {
  acid: "var(--color-acid)",
  bubble: "var(--color-bubble)",
  lemon: "var(--color-lemon)",
  grape: "var(--color-grape)",
} as const;

export function Tape({
  tone = "acid",
  tilt = -6,
  className,
}: {
  tone?: keyof typeof washiTones;
  tilt?: number;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      style={
        { "--washi": washiTones[tone], rotate: `${tilt}deg` } as CSSProperties
      }
      className={cn("washi z-10", className)}
    />
  );
}

/**
 * A cream paper note taped onto a surface, slightly tilted, with a torn
 * bottom edge. Puts copy on high-contrast paper instead of straight onto a
 * saturated background.
 */
export function PaperNote({
  tilt = -1.5,
  tape = "acid",
  className,
  children,
}: {
  tilt?: number;
  tape?: keyof typeof washiTones | null;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className="paper-shadow relative" style={{ rotate: `${tilt}deg` }}>
      {tape && (
        <Tape tone={tape} className="-top-3 left-1/2 -translate-x-1/2" />
      )}
      <div
        className={cn(
          "bg-paper border-ink border-chip torn-bottom px-6 pb-9 pt-7",
          className,
        )}
      >
        {children}
      </div>
    </div>
  );
}

/** Loose hand-drawn arrow; draws itself once on first paint. */
export function HandArrow({
  className,
  delay = 0,
}: {
  className?: string;
  delay?: number;
}) {
  return (
    <svg
      viewBox="0 0 120 80"
      fill="none"
      aria-hidden="true"
      className={cn("pointer-events-none overflow-visible", className)}
      style={{ "--draw-delay": `${delay}ms` } as CSSProperties}
    >
      <path
        className="draw-stroke"
        pathLength={1}
        d="M6 14 C 38 4, 64 22, 58 40 C 54 54, 30 48, 40 34 C 52 18, 92 30, 108 64"
        stroke="currentColor"
        strokeWidth={3.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        className="draw-stroke"
        pathLength={1}
        style={{ "--draw-delay": `${delay + 650}ms` } as CSSProperties}
        d="M92 60 L108 64 L106 47"
        stroke="currentColor"
        strokeWidth={3.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Marker squiggle to underline a word; sits under its text span. */
export function Squiggle({ children }: { children: ReactNode }) {
  return (
    <span className="relative inline-block">
      {children}
      <svg
        viewBox="0 0 200 18"
        preserveAspectRatio="none"
        fill="none"
        aria-hidden="true"
        className="text-bubble pointer-events-none absolute -bottom-3 left-0 h-3.5 w-full overflow-visible"
      >
        <path
          className="draw-stroke"
          pathLength={1}
          style={{ "--draw-delay": "300ms" } as CSSProperties}
          d="M3 11 C 28 3, 46 16, 72 9 S 118 3, 142 10 S 182 15, 197 6"
          stroke="currentColor"
          strokeWidth={5}
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

/** Four-point sparkle that pops gently, as on the landing. */
export function Sparkle({
  className,
  delay = 0,
}: {
  className?: string;
  delay?: number;
}) {
  return (
    <svg
      viewBox="0 0 40 40"
      aria-hidden="true"
      className={cn("spark-pop pointer-events-none", className)}
      style={{ animationDelay: `${delay}s` }}
    >
      <path
        d="M20 2 C 22 14, 26 18, 38 20 C 26 22, 22 26, 20 38 C 18 26, 14 22, 2 20 C 14 18, 18 14, 20 2 Z"
        fill="currentColor"
        stroke="var(--color-ink)"
        strokeWidth={2.5}
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The landing's marquee band as a tilted ribbon. Copy is repeated for the
 * loop; the duplicate is hidden from assistive tech. Hover or press pauses.
 */
export function Ribbon({
  items,
  tilt = -2,
  seconds = 24,
  reverse = false,
  tone = "ink",
  className,
}: {
  items: readonly string[];
  tilt?: number;
  /** Seconds for one pass of `items`; higher is slower. */
  seconds?: number;
  reverse?: boolean;
  tone?: "ink" | "bubble";
  className?: string;
}) {
  // Each half repeats the items until it is wider than any screen, and both
  // halves are identical (separator spacing included), so translating by -50%
  // lands exactly where it started: the band is always full and the loop has
  // no visible start or end.
  // ~240 characters per half covers a 2560px screen at this type size.
  const setLength = items.reduce((total, item) => total + item.length + 3, 0);
  const repeats = Math.max(2, Math.ceil(240 / setLength));
  const half = Array.from({ length: repeats }, () => items).flat();
  return (
    <div
      style={{ rotate: `${tilt}deg` }}
      className={cn(
        "ribbon border-ink border-y-(length:--border-surface) overflow-hidden py-2.5",
        tone === "ink" ? "bg-ink text-paper" : "bg-bubble text-ink",
        className,
      )}
    >
      <div
        style={{
          // Same speed whatever the repetition count.
          animationDuration: `${seconds * repeats}s`,
          animationDirection: reverse ? "reverse" : "normal",
        }}
        className="ribbon-marquee font-display flex w-max select-none whitespace-nowrap text-lg font-extrabold uppercase"
      >
        {[0, 1].map((copy) => (
          <span
            key={copy}
            aria-hidden={copy === 1 ? true : undefined}
            className="flex shrink-0"
          >
            {half.map((item, index) => (
              <span key={index} className="flex items-center gap-6 pr-6">
                {item}
                <span aria-hidden="true">✦</span>
              </span>
            ))}
          </span>
        ))}
      </div>
    </div>
  );
}
